/** Minimal semver: parse, compare, and match the range forms designs use in `migrate`. */

export interface SemVer {
  major: number;
  minor: number;
  patch: number;
  prerelease?: string;
}

const RE = /^v?(\d+)\.(\d+)\.(\d+)(?:-([0-9A-Za-z.-]+))?$/;

export function parseSemver(v: string): SemVer {
  const m = RE.exec(v.trim());
  if (!m) throw new Error(`Invalid semver "${v}" (expected MAJOR.MINOR.PATCH)`);
  return { major: +m[1], minor: +m[2], patch: +m[3], prerelease: m[4] };
}

export function isSemver(v: string): boolean {
  return RE.test(v.trim());
}

export function compareSemver(a: string, b: string): number {
  const x = parseSemver(a);
  const y = parseSemver(b);
  if (x.major !== y.major) return x.major - y.major;
  if (x.minor !== y.minor) return x.minor - y.minor;
  if (x.patch !== y.patch) return x.patch - y.patch;
  if (x.prerelease && !y.prerelease) return -1;
  if (!x.prerelease && y.prerelease) return 1;
  if (x.prerelease && y.prerelease) return x.prerelease < y.prerelease ? -1 : x.prerelease > y.prerelease ? 1 : 0;
  return 0;
}

export function maxSemver(versions: string[]): string | undefined {
  return versions.slice().sort(compareSemver).at(-1);
}

/**
 * Range forms: exact "1.2.3", wildcard "1.x" / "1.2.x" / "*", caret "^1.2.0", tilde "~1.2.0",
 * comparators ">=1.0.0 <2.0.0". Enough for migrate keys and brand pack pins.
 */
export function satisfies(version: string, range: string): boolean {
  const v = parseSemver(version);
  const r = range.trim();
  if (r === "*" || r === "x" || r === "") return true;
  if (r.includes(" ")) return r.split(/\s+/).every((part) => satisfies(version, part));

  if (r.startsWith("^")) {
    const b = parseSemver(r.slice(1));
    if (b.major > 0) return v.major === b.major && compareSemver(version, r.slice(1)) >= 0;
    if (b.minor > 0) return v.major === 0 && v.minor === b.minor && compareSemver(version, r.slice(1)) >= 0;
    return v.major === 0 && v.minor === 0 && v.patch === b.patch;
  }
  if (r.startsWith("~")) {
    const b = parseSemver(r.slice(1));
    return v.major === b.major && v.minor === b.minor && compareSemver(version, r.slice(1)) >= 0;
  }
  const cmp = /^(>=|<=|>|<|=)(.+)$/.exec(r);
  if (cmp) {
    const c = compareSemver(version, cmp[2]);
    switch (cmp[1]) {
      case ">=": return c >= 0;
      case "<=": return c <= 0;
      case ">": return c > 0;
      case "<": return c < 0;
      default: return c === 0;
    }
  }
  const wild = /^v?(\d+)(?:\.(\d+|x|\*))?(?:\.(\d+|x|\*))?$/.exec(r);
  if (wild) {
    const [, maj, min, pat] = wild;
    if (v.major !== +maj) return false;
    if (min === undefined || min === "x" || min === "*") return true;
    if (v.minor !== +min) return false;
    if (pat === undefined || pat === "x" || pat === "*") return true;
    return v.patch === +pat;
  }
  throw new Error(`Unsupported version range "${range}"`);
}
